import React, { useEffect, useState } from "react";

import { trackMixpanel, usePageLoadingTransitions, useTranslation } from "utils";

import { Button, Flex, FormBodyWrapper, FormFlowNavBar, InfoAccordion, InfoAccordionItem, PageStatus, Spin, StickyFooterButtons } from "components";

export interface FormPage {
  key: string;
  status: PageStatus | null;
  navigationTitle: string;
  navigationCategory?: string;
  pageSubtitle?: string;
  body: () => JSX.Element;
  accordionItems?: InfoAccordionItem[];
  accordionFooter?: React.ReactNode;
}

export type FormFlowPageProps = {
  formPages: FormPage[];
  navBarTitle?: string;
  initialStep?: number;
  currentStep?: number;
  onStepChange?: (step: number) => void;
  editMode?: boolean;
  completionPercent?: number;
  isLoading?: boolean;
  enablePageTransitions?: boolean;
  transitionDuration?: number;
  onClose: () => void;
};

export const FormFlowScreen = ({
  formPages,
  navBarTitle,
  initialStep,
  currentStep,
  onStepChange,
  editMode,
  completionPercent,
  isLoading,
  enablePageTransitions = true,
  onClose,
}: FormFlowPageProps) => {
  const { t } = useTranslation("nav");
  const [internalPageIndex, setInternalPageIndex] = useState(initialStep ?? 0);
  const activePageIndex = currentStep !== undefined ? currentStep : internalPageIndex;

  const currentPage = formPages[activePageIndex];
  const hasAccordion = currentPage?.accordionItems && currentPage.accordionItems.length > 0;

  const { isTransitioning, startTransition } = usePageLoadingTransitions();

  const setActivePageIndex = (index: number) => {
    const updateIndex = () => {
      if (currentStep !== undefined) {
        onStepChange?.(index);
      } else {
        setInternalPageIndex(index);
      }
    };

    if (enablePageTransitions) {
      startTransition(updateIndex);
    } else {
      updateIndex();
    }
  };

  const handleClose = () => {
    trackMixpanel("CloseFormFlowScreen", { formType: navBarTitle });
    onClose();
  };

  const resetPageScroll = () => {
    const content = document.querySelector(".form-flow-content");
    if (content) content.scrollTop = 0;
  };

  const handleChangeActivePageIndex = (diff: number) => {
    setActivePageIndex(activePageIndex + diff);
  };

  const handleNavbarClick = (index: number) => {
    setActivePageIndex(index);
  };

  useEffect(() => {
    resetPageScroll();
  }, [activePageIndex]);

  useEffect(() => {
    if (currentStep === undefined && Number.isInteger(initialStep)) {
      setInternalPageIndex(initialStep ?? 0);
    }
  }, [initialStep, currentStep]);

  const renderPageBody = () => (
    <FormBodyWrapper title={currentPage.navigationTitle} subtitle={currentPage.pageSubtitle}>
      {currentPage.body()}
    </FormBodyWrapper>
  );

  return (
    <div className="form-flow-screen-wrapper">
      <div className="form-flow-screen">
        <div className="form-flow-nav padding-200">
          <FormFlowNavBar
            activePageNumber={activePageIndex}
            handleSetActivePageNumber={handleNavbarClick}
            pages={formPages.map((step) => ({
              title: step.navigationTitle,
              key: step.key,
              status: step.status,
              category: step.navigationCategory,
            }))}
          />
        </div>

        <div className="form-flow-content">
          <Flex className={`padding-200 padding-top-400 ${hasAccordion ? "with-accordion" : ""}`} gap={24}>
            <div className="form-flow-body-loading-wrapper">
              <div className={`form-flow-body ${isTransitioning ? "transitioning" : ""}`}>{renderPageBody()}</div>
              {isLoading ||
                (isTransitioning && (
                  <div className="form-transition-overlay">
                    <Spin />
                  </div>
                ))}
            </div>

            <Flex className="form-flow-accordion" gap={16} vertical>
              <InfoAccordion items={currentPage.accordionItems ?? []} />
              {currentPage.accordionFooter ?? null}
            </Flex>
          </Flex>

          <StickyFooterButtons absolute progressBarPercent={completionPercent}>
            <Flex justify="space-between">
              {!editMode &&
                (activePageIndex > 0 ? (
                  <>
                    <Button
                      dataTestId="overlay-back-button"
                      type="tertiary"
                      onClick={() => handleChangeActivePageIndex(-1)}
                      className="footer-button"
                      disabled={isTransitioning}
                    >
                      {t("overlay.back")}
                    </Button>
                    <Button
                      className="footer-button"
                      dataTestId="overlay-next-button"
                      type="primary"
                      onClick={activePageIndex + 1 === formPages.length ? handleClose : () => handleChangeActivePageIndex(1)}
                      disabled={isTransitioning}
                    >
                      {activePageIndex + 1 === formPages.length ? t("overlay.saveAndClose") : t("overlay.saveAndContinue")}
                    </Button>
                  </>
                ) : (
                  <Button
                    dataTestId="overlay-start-button"
                    block
                    type="primary"
                    onClick={() => handleChangeActivePageIndex(1)}
                    className="footer-button"
                    disabled={isTransitioning}
                  >
                    {t("overlay.saveAndContinue")}
                  </Button>
                ))}
            </Flex>
          </StickyFooterButtons>
        </div>
      </div>
    </div>
  );
};
